import { Component } from '@theme/component';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const NAV_QUERY = window.matchMedia('(min-width: 1200px)');

/**
 * Site header: solid-on-scroll state and the mobile menu dialog.
 *
 * @typedef {object} AcHeaderRefs
 * @property {HTMLButtonElement} toggle - Opens the mobile menu
 * @property {HTMLDialogElement} dialog - The mobile menu
 *
 * @extends {Component<AcHeaderRefs>}
 */
class AcHeaderComponent extends Component {
  requiredRefs = ['toggle', 'dialog'];

  /** @type {AbortController | undefined} */
  #scrollController;

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#bindScroll, { signal });
    NAV_QUERY.addEventListener('change', (event) => event.matches && this.closeMenu(), { signal });
    this.refs.dialog.addEventListener('close', this.#handleClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#scrollController?.abort();
  }

  // The scroll container switches between the document and .page-wrapper at 990px.
  #bindScroll = () => {
    this.#scrollController?.abort();
    this.#scrollController = new AbortController();

    getScrollEventTarget().addEventListener('scroll', this.#update, {
      passive: true,
      signal: this.#scrollController.signal,
    });
    this.#update();
  };

  #update = () => {
    this.classList.toggle('is-scrolled', getScrollTop() > 24);
  };

  openMenu() {
    const { dialog, toggle } = this.refs;
    if (dialog.open) return;

    dialog.showModal();
    toggle.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  closeMenu() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
  }

  /**
   * Closes the menu when a link is chosen, so in-page anchors don't land behind the dialog.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    // The framework proxies event.target to the on:click element, so read the real origin.
    const origin = event.composedPath()[0];
    if (origin instanceof Element && origin.closest('a')) this.closeMenu();
  }

  #handleClose = () => {
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    this.refs.toggle.focus();
  };
}

if (!customElements.get('ac-header-component')) {
  customElements.define('ac-header-component', AcHeaderComponent);
}
